import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { ResolvedIntake } from '@/types/team-settings';

export interface UseTeamIntakeResult {
  /** null while loading, on error, or when disabled — callers fall back
   *  to the flexible (classify) flow; the backend gates regardless. */
  intake: ResolvedIntake | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

/**
 * The team's resolved intake mode for the Request Report page. Clients
 * resolve their own team (no param); admins pass the selected team.
 */
export function useTeamIntake(params: { teamId?: string; enabled?: boolean }): UseTeamIntakeResult {
  const { teamId, enabled = true } = params;
  const [intake, setIntake] = useState<ResolvedIntake | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOnce = useCallback(async () => {
    if (!enabled) {
      setIntake(null);
      return;
    }
    setIsLoading(true);
    try {
      setIntake(await vnextApiClient.getCurrentIntake(teamId));
    } catch (err) {
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
      setIntake(null);
    } finally {
      setIsLoading(false);
    }
  }, [teamId, enabled]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  return { intake, isLoading, refetch: fetchOnce };
}
